import { useCallback, useMemo, useRef, useState } from 'react';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { Container, KeenIcon } from '@/components';
import { useAuthContext } from '@/auth';
import { getAccessToken, createDataTableOptions, createDataTableConfig, renderMultiAvatars } from '@/utils';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { CreditCard, Eye, Loader2, Send } from 'lucide-react';
import { DateInput, FilterContainer, ColumnToggleDropdown, SelectInput } from '@/components/TableFilter'
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import companiesService from '@/services/companies/companiesService';
DataTable.use(DT);

const paymentStatusClass = {
  pending: 'badge-warning',
  paid: 'badge-success',
  failed: 'badge-danger',
};

const statusClass = {
  active: 'badge-success',
  inactive: 'badge-danger',
  pending: 'badge-warning',
  cancelled: 'badge-danger',
  canceled: 'badge-danger',
  expired: 'badge-danger',
  deleted: 'badge-danger',
};

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  return value;
};

const getFullName = (user) => {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  return fullName || '-';
};

const getCurrencyValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  return `$${value}`;
};

const renderStatusBadge = (value, classMap) => {
  if (!value) {
    return '-';
  }

  const normalizedValue = String(value).toLowerCase();
  const badgeClass = classMap[normalizedValue] || 'badge-secondary';

  return `<span class="capitalize badge badge-pill badge-outline ${badgeClass} gap-1 items-center">
    <span class="badge badge-dot size-1.5 ${badgeClass}"></span>
    ${normalizedValue.split('_').join(' ')}
  </span>`;
};

const SubscriptionUsers = () => {
  const { logout, auth } = useAuthContext();
  const id = auth?.user?.company_id;
  const tableRef = useRef(null);
  const token = getAccessToken();
  const hasCompanyIdParam = Boolean(id);
  const filterRef = useRef({
    tenant_id: id,
  });
  filterRef.current.tenant_id = id;
  const columnOrderStorageKey = typeof window !== 'undefined'
    ? `column-toggle-order:${window.location.pathname}`
    : null;

  const [showFilter, setShowFilter] = useState(false)
  const [companyName, setCompanyName] = useState('');
  const [filterData, setFilterData] = useState({
    tenant_id: id,
    status: null,
    payment_status: null,
    from_date: null,
    to_date: null,
    date_range: null,
  });

  const navigate = useNavigate();
  const [startingCheckout, setStartingCheckout] = useState(false);

  // Straight to Stripe: package and cycle come from what the firm already had,
  // so there is nothing to choose and no reason for a form first.
  const startRenewalCheckout = useCallback(async () => {
    setStartingCheckout(true);

    try {
      const { status, data } = await companiesService.createRenewalCheckout();

      if (status && data?.url) {
        window.location.href = data.url;
        return;
      }

      toast.error('Could not start checkout', { description: 'Please try again in a moment.' });
      setStartingCheckout(false);
    } catch (error) {
      toast.error('Could not start checkout', {
        description: error?.message || 'Please try again later.',
      });
      setStartingCheckout(false);
    }
  }, []);

  const tableColumns = useMemo(() => [
    {
      data: 'subscriptions',
      title: 'Customer Name',
      name: 'paid_by_user.name',
      orderable: false,
      render: (_, __, row) => {
        const firstSub = row.subscriptions?.[0];
        return getFullName(firstSub?.user);
      },
    },
    {
      data: 'package_name',
      title: 'License',
      name: 'package_name',
      orderable: false,
      render: (data) => getDisplayValue(data),
    },
    {
      data: 'billing_cycle',
      title: 'Billing Cycle',
      name: 'billing_cycle',
      render: (data) => getDisplayValue(data),
    },
    {
      data: 'total_amount',
      title: 'Price',
      name: 'total_amount',
      orderable: false,
      render: (data) => getCurrencyValue(data),
    },
    {
      data: 'subscriptions',
      title: 'Users',
      name: 'subscriptions',
      orderable: false,
      render: (_, __, row) => `<div id="users-cell-${row.DT_RowIndex}"></div>`,
    },
    {
      data: 'starts_at',
      title: 'Purchase Date',
      name: 'starts_at',
      render: (data) => getDisplayValue(data),
    },
    {
      data: 'expires_at',
      title: 'Expiry Date',
      name: 'expires_at',
      render: (data) => getDisplayValue(data),
    },
    {
      data: 'status',
      title: 'Payment Status',
      name: 'status',
      render: (_, __, row) => {
        const status = row.status;
        return renderStatusBadge(status, paymentStatusClass);
      }
    },
    {
      title: 'Actions',
      data: null,
      name: 'actions',
      orderable: false,
      searchable: false,
      render: (_, __, row) => `<div id="action-cell-${row.DT_RowIndex}"></div>`,
    },
  ], []);

  const columns = useMemo(() => {
    const cols = [...tableColumns];
    if (!hasCompanyIdParam) {
      cols.splice(1, 0, {
        data: 'company.name',
        title: 'Company Name',
        name: 'company.name',
        orderable: false,
        render: (_, __, row) => getDisplayValue(row?.company?.name),
      });
    }
    return cols;
  }, [hasCompanyIdParam, tableColumns]);

  const defaultVisibleColumns = useMemo(() => {
    const cols = [
      'paid_by_user.name',
      'package_name',
      'billing_cycle',
      'total_amount',
      'subscriptions',
      'starts_at',
      'expires_at',
      'status',
      'actions',
    ];
    if (!hasCompanyIdParam) {
      cols.splice(1, 0, 'company.name');
    }
    return cols;
  }, [hasCompanyIdParam]);

  if (typeof window !== 'undefined' && columnOrderStorageKey) {
    try {
      const raw = window.localStorage.getItem(columnOrderStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.includes('actions')) {
          const cleaned = parsed.filter((name) => name !== 'actions');
          cleaned.push('actions');
          window.localStorage.setItem(columnOrderStorageKey, JSON.stringify(cleaned));
        }
      }
    } catch (error) {
      // ignore invalid storage
    }
  }

  const ajaxConfig = useMemo(() => (
    createDataTableConfig('/admin/billing/data_table_list', filterRef, token, logout)
  ), [logout, token]);

  const renderCustomAction = useCallback((row) => {
    const { id, subscriptions } = row;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="text-gray-600">
            <KeenIcon icon="dots-vertical" className="text-[18px]" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onClick={() => navigate(`/billing/summary/invoice-preview/${id}`)}>
            <Eye className="mr-0.5 h-4 w-4 cursor-pointer" /> View Invoice
          </DropdownMenuItem>
          {subscriptions?.some(sub => sub?.status === 'expired') && (
            <DropdownMenuItem disabled={startingCheckout} onClick={startRenewalCheckout}>
              {startingCheckout ? (
                <><Loader2 className="mr-0.5 h-4 w-4 animate-spin" /> Opening Stripe...</>
              ) : (
                <><CreditCard className="mr-0.5 h-4 w-4 cursor-pointer" /> Pay Now</>
              )}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }, [navigate, startRenewalCheckout, startingCheckout]);

  const options = useMemo(() => createDataTableOptions({
    order: [[6, 'desc']],
    initComplete: function () {
      const api = this.api();
      api.columns().every(function () {
        const column = this;
        const name = column.settings()[0].aoColumns[column.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) {
          column.visible(false);
        }
      });
    },
    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: 'current' }).data();

      if (hasCompanyIdParam && rows.length > 0) {
        setCompanyName(rows[0]?.company?.name || '');
      }

      rows.each((row) => {
        // Render Users
        const usersContainer = document.getElementById(`users-cell-${row.DT_RowIndex}`);
        if (usersContainer) {
          if (!usersContainer._reactRoot) {
            usersContainer._reactRoot = createRoot(usersContainer);
          }
          const users = row.subscriptions?.map(sub => sub.user).filter(Boolean) || [];
          usersContainer._reactRoot.render(renderMultiAvatars(users));
        }

        // Render Actions
        const actionContainer = document.getElementById(`action-cell-${row.DT_RowIndex}`);
        if (actionContainer) {
          if (!actionContainer._reactRoot) {
            actionContainer._reactRoot = createRoot(actionContainer);
          }
          actionContainer._reactRoot.render(renderCustomAction(row));
        }
      });
    },
  }), [defaultVisibleColumns, hasCompanyIdParam, renderCustomAction]);

  const refresh = () => {
    const table = tableRef.current.dt();
    table.ajax.reload();
  };

  const resetFilters = () => {
    const cleared = {
      tenant_id: id,
      status: null,
      payment_status: null,
      from_date: null,
      to_date: null,
      date_range: null,
    };

    setFilterData(cleared);
    filterRef.current = cleared;
    refresh()
  };

  const handleToggleFilter = () => {
    setShowFilter(prev => !prev)
  }

  const handleFilterChange = (key, val) => {
    setFilterData((prev) => {
      const updated = { ...prev, [key]: val };
      filterRef.current = updated;
      //console.log('updated',updated)
      return updated;
    });
  };

  return (
    <>
      <FilterContainer
        show={showFilter}
        onApply={() => refresh()}
        onReset={resetFilters}
      >
        {/* <SelectInput
          label="Status"
          value={filterData.status}
          onChange={(val) => handleFilterChange('status', val)}
          staticOptions={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'expired', label: 'Expired' },
            { value: 'cancelled', label: 'Cancelled' },
            { value: 'deleted', label: 'Deleted' },
          ]}
          customClass="sm:w-[32%] w-full"
        /> */}

        <SelectInput
          label="Payment Status"
          value={filterData.status}
          onChange={(val) => handleFilterChange('status', val)}
          staticOptions={[
            { value: 'paid', label: 'Paid' },
            { value: 'failed', label: 'Failed' },
            { value: 'pending', label: 'Pending' },
          ]}
          customClass="sm:w-[32%] w-full"
        />

        <DateInput
          customClass="sm:w-[32%] w-full"
          label="Created At - (From, To)"
          mode="range"
          value={filterData.date_range}
          onChange={(range) => {
            handleFilterChange('from_date', range?.from ?? null);
            handleFilterChange('to_date', range?.to ?? null);
            handleFilterChange('date_range', range);
          }}
        />
      </FilterContainer>
      <div className="min-w-full mb-4 card">
        <div className="card-header flex-wrap gap-3">
          <div className='flex items-center gap-1'>
            <ColumnToggleDropdown
              tableRef={tableRef}
              columns={columns}
              defaultVisibleColumns={defaultVisibleColumns}
              storageKey={columnOrderStorageKey}
              pinnedLastColumns={['actions']}
            />
          </div>

          <div>
            <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
              <KeenIcon icon="filter" className="w-fit btn-icon" />
              {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
            </button>
          </div>
        </div>

        <DataTable
          id="admin-company-billing-detail-table"
          ajax={ajaxConfig}
          columns={columns}
          options={options}
          ref={tableRef}
        />
      </div>
    </>
  );
};

export { SubscriptionUsers };